import React, { useState, useEffect, useMemo } from 'react';
import { descargarAuditoriaFacturas, obtenerFacturas } from '../servicios/api';
import { Archive, Eye, Download, Search } from 'lucide-react';
import { useAuth } from '../contexto/AuthContext';

const obtenerFechaFactura = (fecha) => {
    if (!fecha) return null;
    const parsed = new Date(fecha);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const obtenerPeriodoTrimestral = (fecha) => {
    const fechaISO = String(fecha || '').match(/^(\d{4})-(\d{2})-\d{2}/);
    if (fechaISO) {
        const mes = Number(fechaISO[2]);
        return mes >= 1 && mes <= 12
            ? { anio: Number(fechaISO[1]), trimestre: Math.floor((mes - 1) / 3) + 1 }
            : null;
    }

    const parsed = obtenerFechaFactura(fecha);
    return parsed
        ? { anio: parsed.getFullYear(), trimestre: Math.floor(parsed.getMonth() / 3) + 1 }
        : null;
};

const formatearFecha = (fecha) => {
    if (!fecha) return '—';
    const dateOnly = String(fecha).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnly) {
        return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
    }
    const parsed = obtenerFechaFactura(fecha);
    return parsed ? parsed.toLocaleDateString('es-ES') : '—';
};

const getStatusColor = (estado) => {
    switch (estado?.toLowerCase()) {
        case 'cobrada': return 'text-green-400 bg-green-500/20';
        case 'emitida': return 'text-blue-400 bg-blue-500/20';
        case 'vencida': return 'text-yellow-400 bg-yellow-500/20';
        case 'anulada': return 'text-red-400 bg-red-500/20';
        default: return 'text-gray-400 bg-gray-500/20';
    }
};

const ListaFacturas = () => {
    const { user } = useAuth();
    const [facturas, setFacturas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [trimestreSeleccionado, setTrimestreSeleccionado] = useState('');
    const [exportandoAuditoria, setExportandoAuditoria] = useState(false);
    const [errorExportacion, setErrorExportacion] = useState('');

    const handleExportarAuditoria = async () => {
        try {
            setExportandoAuditoria(true);
            setErrorExportacion('');
            const archivo = await descargarAuditoriaFacturas();
            const url = URL.createObjectURL(archivo);
            const enlace = document.createElement('a');
            enlace.href = url;
            enlace.download = `auditoria_verifactu_${new Date().toISOString().slice(0, 10)}.zip`;
            document.body.appendChild(enlace);
            enlace.click();
            enlace.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            let responseMessage = err.response?.data?.mensaje;
            if (err.response?.data instanceof Blob) {
                try {
                    const responseBody = JSON.parse(await err.response.data.text());
                    responseMessage = responseBody.mensaje;
                } catch (parseError) {
                    console.error('No se pudo interpretar el error de exportación:', parseError);
                }
            }
            setErrorExportacion(typeof responseMessage === 'string'
                ? responseMessage
                : 'No se pudo descargar el paquete de auditoría. Inténtalo de nuevo.');
            console.error('Error al exportar auditoría de facturas:', err);
        } finally {
            setExportandoAuditoria(false);
        }
    };

    useEffect(() => {
        const cargarFacturas = async () => {
            try {
                setLoading(true);
                const response = await obtenerFacturas();
                if (response.exito && Array.isArray(response.facturas)) {
                    setFacturas(response.facturas);
                } else {
                    setError(response.mensaje || 'No se pudieron cargar las facturas.');
                }
            } catch (err) {
                setError(err.response?.data?.mensaje || 'Error de conexión al obtener las facturas.');
                console.error('Error al cargar facturas:', err);
            } finally {
                setLoading(false);
            }
        };

        cargarFacturas();
    }, []);

    const facturasOrdenadas = useMemo(() => [...facturas].sort((a, b) => {
        const fechaA = obtenerFechaFactura(a.fecha_emision)?.getTime() || 0;
        const fechaB = obtenerFechaFactura(b.fecha_emision)?.getTime() || 0;
        return fechaB - fechaA || Number(b.id_factura || 0) - Number(a.id_factura || 0);
    }), [facturas]);

    const facturasFiltradas = useMemo(() => {
        const termino = searchTerm.trim().toLocaleLowerCase('es');
        if (!termino) return facturasOrdenadas;

        return facturasOrdenadas.filter(factura =>
            String(factura.numero_factura || '').toLocaleLowerCase('es').includes(termino) ||
            String(factura.nombre_cliente || '').toLocaleLowerCase('es').includes(termino)
        );
    }, [facturasOrdenadas, searchTerm]);

    const trimestres = useMemo(() => {
        const grupos = new Map();
        facturasOrdenadas.forEach(factura => {
            const periodo = obtenerPeriodoTrimestral(factura.fecha_emision);
            const anio = periodo?.anio ?? 'Sin fecha';
            const trimestre = periodo?.trimestre ?? 0;
            const clave = `${anio}-T${trimestre}`;
            if (!grupos.has(clave)) {
                grupos.set(clave, {
                    clave,
                    anio,
                    trimestre,
                    facturas: []
                });
            }
            grupos.get(clave).facturas.push(factura);
        });

        return [...grupos.values()].sort((a, b) =>
            String(b.anio).localeCompare(String(a.anio), undefined, { numeric: true }) ||
            b.trimestre - a.trimestre
        );
    }, [facturasOrdenadas]);

    const periodoSeleccionado = trimestreSeleccionado || trimestres[0]?.clave || '';
    const facturasDelTrimestre = facturasFiltradas.filter(factura => {
        const periodo = obtenerPeriodoTrimestral(factura.fecha_emision);
        const clave = periodo ? `${periodo.anio}-T${periodo.trimestre}` : 'Sin fecha-T0';
        return clave === periodoSeleccionado;
    });
    const ultimasFacturas = facturasFiltradas.slice(0, 5);
    const tituloTrimestreSeleccionado = trimestres.find(grupo => grupo.clave === periodoSeleccionado);

    const renderFilas = (lista) => lista.map(factura => (
        <tr key={factura.id_factura} className="hover:bg-slate-700/50">
            <td className="px-4 py-3 font-mono text-blue-400">{factura.numero_factura || '—'}</td>
            <td className="px-4 py-3">{formatearFecha(factura.fecha_emision)}</td>
            <td className="px-4 py-3">{factura.nombre_cliente || '—'}</td>
            <td className="px-4 py-3 text-right font-semibold">{Number(factura.total || 0).toFixed(2)} €</td>
            <td className="px-4 py-3 text-center">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(factura.estado)}`}>
                    {factura.estado || 'Desconocido'}
                </span>
                <span className="block mt-1 text-xs text-gray-400">
                    {factura.tipo_factura === 'rectificativa' ? 'Rectificativa' : 'Ordinaria'} · Veri*Factu: {factura.estado_aeat || 'pendiente'}
                </span>
            </td>
            <td className="px-4 py-3">
                <div className="flex justify-center items-center gap-2">
                    <button type="button" title="Ver detalle" aria-label={`Ver factura ${factura.numero_factura}`} className="text-gray-400 hover:text-blue-400"><Eye size={16} /></button>
                    <button type="button" title="Descargar PDF" aria-label={`Descargar factura ${factura.numero_factura}`} className="text-gray-400 hover:text-green-400"><Download size={16} /></button>
                </div>
            </td>
        </tr>
    ));

    const renderTabla = (lista, emptyMessage) => (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
                <thead className="bg-slate-700">
                    <tr>
                        <th className="px-4 py-3 text-left font-semibold">Número</th>
                        <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                        <th className="px-4 py-3 text-left font-semibold">Cliente</th>
                        <th className="px-4 py-3 text-right font-semibold">Total</th>
                        <th className="px-4 py-3 text-center font-semibold">Estado / Veri*Factu</th>
                        <th className="px-4 py-3 text-center font-semibold">Acciones</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                    {lista.length ? renderFilas(lista) : (
                        <tr><td colSpan="6" className="text-center py-8 text-gray-500">{emptyMessage}</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="font-sans bg-gray-900 text-gray-300 text-sm p-4 min-h-screen">
            <div className="max-w-7xl mx-auto space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h1 className="text-2xl font-bold text-white">Listado de Facturas</h1>
                    {user?.rol === 'administrador' && (
                        <button
                            type="button"
                            onClick={handleExportarAuditoria}
                            disabled={exportandoAuditoria}
                            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:cursor-wait disabled:opacity-60"
                            title="Exporta facturas, conceptos y eventos de auditoría como JSON/XML sin firma digital."
                        >
                            <Archive size={16} />
                            {exportandoAuditoria ? 'Preparando exportación…' : 'Exportar auditoría'}
                        </button>
                    )}
                </div>
                {errorExportacion && <p role="alert" className="text-red-400">{errorExportacion}</p>}

                <div className="bg-slate-800 rounded-lg shadow-lg border border-slate-700">
                    <div className="p-4 border-b border-slate-700">
                        <div className="relative w-full md:max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="search"
                                placeholder="Buscar por nº de factura o cliente..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full bg-slate-700 border border-slate-600 rounded-md pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {loading && <p className="p-4 text-center">Cargando facturas...</p>}
                    {error && <p className="p-4 text-center text-red-400">{error}</p>}

                    {!loading && !error && (
                        <div className="space-y-6 p-4">
                            <section className="rounded-lg border border-blue-500/30 overflow-hidden">
                                <div className="flex flex-wrap items-center justify-between gap-2 bg-blue-500/10 px-4 py-3">
                                    <h2 className="text-base font-semibold text-blue-200">Últimas facturas</h2>
                                    <span className="text-xs text-gray-400">Las 5 más recientes · {ultimasFacturas.length}</span>
                                </div>
                                {renderTabla(ultimasFacturas, 'No hay facturas recientes.')}
                            </section>

                            <section>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                                    <div>
                                        <h2 className="text-lg font-semibold text-white">Consultar facturas por trimestre</h2>
                                        <p className="text-xs text-gray-400 mt-1">Selecciona un período para ver todas sus facturas.</p>
                                    </div>
                                    <select
                                        value={periodoSeleccionado}
                                        onChange={event => setTrimestreSeleccionado(event.target.value)}
                                        className="w-full sm:w-auto min-w-56 rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        aria-label="Seleccionar trimestre de facturación"
                                    >
                                        {trimestres.map(grupo => (
                                            <option key={grupo.clave} value={grupo.clave}>
                                                {grupo.trimestre ? `Trimestre ${grupo.trimestre} · ${grupo.anio}` : 'Facturas sin fecha'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {tituloTrimestreSeleccionado ? (
                                    <div className="rounded-lg border border-slate-700 overflow-hidden">
                                        <div className="flex items-center justify-between bg-slate-700 px-4 py-3">
                                            <h3 className="font-semibold text-white">
                                                {tituloTrimestreSeleccionado.trimestre
                                                    ? `Trimestre ${tituloTrimestreSeleccionado.trimestre} · ${tituloTrimestreSeleccionado.anio}`
                                                    : 'Facturas sin fecha'}
                                            </h3>
                                            <span className="text-xs text-gray-300">{facturasDelTrimestre.length} facturas</span>
                                        </div>
                                        {renderTabla(facturasDelTrimestre, 'No hay facturas en este trimestre.')}
                                    </div>
                                ) : (
                                    <div className="rounded-lg border border-slate-700 p-8 text-center text-gray-500">
                                        No se encontraron facturas.
                                    </div>
                                )}
                            </section>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ListaFacturas;
